// SPDX-License-Identifier: SEE LICENSE
import { useMemo } from 'react';

export function Sparkline({
  data,
  color = '#7fe8c9',
  width = 120,
  height = 32,
  fill = true,
  emptyText = 'awaiting data',
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  /** Placeholder to show when there's not enough data to draw the line.
   *  Pass `null` to render nothing at all (used by skipped / no-capture
   *  tiles so they don't claim data they don't have). */
  emptyText?: string | null;
}) {
  const { line, area } = useMemo(() => {
    if (data.length < 2) return { line: '', area: '' };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const span = max - min;
    // Reserve ~10% headroom top & bottom so the line never glues to an edge.
    const pad = height * 0.1;
    const usableH = height - pad * 2;
    const stepX = width / (data.length - 1);
    const pts = data.map((v, i) => {
      const x = i * stepX;
      // Degenerate (all-equal) series → centred line at 50% height.
      const ratio = span === 0 ? 0.5 : (v - min) / span;
      const y = height - pad - ratio * usableH;
      return [x, y] as const;
    });
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
    const area = `${line} L${width},${height} L0,${height} Z`;
    return { line, area };
  }, [data, width, height]);

  if (!line) {
    if (emptyText === null) return null;
    return (
      <div
        className="flex items-center justify-center text-[10px] text-slate-400"
        style={{ width, height }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <svg width={width} height={height} className="overflow-visible">
      {fill && <path d={area} fill={color} opacity={0.12} />}
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}
